import { z } from 'zod';

/**
 * Public Supabase settings that both apps read from their build environment.
 * They are safe to ship in a bundle (row level security protects the data),
 * but they still come from env files, never from the source code.
 */
export interface SupabasePublicConfig {
  readonly url: string;
  readonly anonKey: string;
}

/** Names of the variables in a given app, used to build a helpful message. */
export interface SupabaseEnvNames {
  readonly url: string;
  readonly anonKey: string;
}

export type SupabaseEnvResult =
  | { readonly ok: true; readonly config: SupabasePublicConfig }
  | {
      readonly ok: false;
      /** Variables that are not set or are empty */
      readonly missing: readonly string[];
      /** Variables that are set but do not look right */
      readonly invalid: readonly string[];
    };

export const SUPABASE_ENV_NAMES = {
  console: { url: 'VITE_SUPABASE_URL', anonKey: 'VITE_SUPABASE_ANON_KEY' },
  mobile: { url: 'EXPO_PUBLIC_SUPABASE_URL', anonKey: 'EXPO_PUBLIC_SUPABASE_ANON_KEY' },
} as const satisfies Record<string, SupabaseEnvNames>;

const urlSchema = z.url({ protocol: /^https?$/ });
// Legacy anon keys are JWTs; new publishable keys start with "sb_publishable_".
const anonKeySchema = z.string().min(20);

/**
 * Validates the raw values without throwing, so an app can show a clear message
 * instead of crashing when a variable is missing.
 */
export function readSupabaseEnv(
  raw: { url?: string | undefined; anonKey?: string | undefined },
  names: SupabaseEnvNames,
): SupabaseEnvResult {
  const missing: string[] = [];
  const invalid: string[] = [];

  const url = raw.url?.trim() ?? '';
  const anonKey = raw.anonKey?.trim() ?? '';

  if (url === '') missing.push(names.url);
  else if (!urlSchema.safeParse(url).success) invalid.push(names.url);

  if (anonKey === '') missing.push(names.anonKey);
  else if (!anonKeySchema.safeParse(anonKey).success) invalid.push(names.anonKey);

  if (missing.length > 0 || invalid.length > 0) {
    return { ok: false, missing, invalid };
  }
  return { ok: true, config: { url, anonKey } };
}
